import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';

const ParkingBookingForm = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        unitId: "",
        slotId: "",
        mobileNo: "",
        employeeName: "",
        vehicleNo: "",
        vehicleCategory: ""
    });

    const [unitList, setUnitList] = useState([]);
    const [parkingSlotList, setParkingSlotList] = useState([]);
    const [vehicleTypeList, setVehicleTypeList] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const unitRes = await apiServices({ method: "GET", url: `/UnitMaster/GetAllUnitMaster` });
            if (unitRes.isSuccess || unitRes.success || unitRes.IsSuccess) setUnitList((unitRes.data || []).map(u => ({label: `${u.UnitName} - ${u.UnitNumber}`, value: u.UnitId})));
            
            const vehicleRes = await apiServices({ method: "GET", url: `/VehicleTypeMaster/getAllVehicleType` });
            if (vehicleRes.isSuccess || vehicleRes.success || vehicleRes.IsSuccess) setVehicleTypeList((vehicleRes.data || []).map(v => ({label: v.VehicleTypename, value: v.VehicleTypeid})));
            
        } catch (error) {
            console.error("Error fetching dropdowns:", error);
            toast.error("Failed to load form options");
        }
    };

    const fetchParkingSlotsAndEmployees = async (selectedUnitId) => {
        try {
            const slotRes = await apiServices({ method: "GET", url: `/ParkingSlotMaster/getallParkingSlotByUnitId/${selectedUnitId}` });
            if (slotRes.isSuccess || slotRes.success || slotRes.IsSuccess) {
                const slots = slotRes.data || [];
                setParkingSlotList(slots.filter(item => item.isActive === 'Active' || item.IsActive === 'Active' || item.isActive === 1).map(s => ({label: s.slotName, value: s.parkingSlotId || s.id})));
            }

            const empRes = await apiServices({ method: "GET", url: `/CommUserDetails/getAllUserDetails` });
            if (empRes.isSuccess || empRes.success || empRes.IsSuccess) {
                const emps = empRes.Commuserdetailsdata || [];
                setEmployeeList(emps.filter(item => item.UnitId == selectedUnitId));
            }
        } catch (error) { console.error(error); }
    };

    const handleUnitChange = (opt) => {
        const unitId = opt ? opt.value : "";
        setFormData({ ...formData, unitId, slotId: "", employeeName: "", mobileNo: "" });
        if (unitId) {
            fetchParkingSlotsAndEmployees(unitId);
        } else {
            setParkingSlotList([]);
            setEmployeeList([]);
        }
    };

    const handleEmployeeChange = (opt) => {
        const selectedFirstName = opt ? opt.value : "";
        const employee = employeeList.find(emp => emp.FirstName === selectedFirstName);
        
        setFormData({
            ...formData,
            employeeName: selectedFirstName,
            mobileNo: employee ? employee.MobileNumber : ""
        });
    };

    const validateForm = () => {
        if (!formData.unitId) return toast.warning("Unit is required"), false;
        if (!formData.slotId) return toast.warning("Parking Slot is required"), false;
        if (!formData.employeeName) return toast.warning("Owner Name is required"), false;
        if (!formData.mobileNo) return toast.warning("Mobile Number is required"), false;
        if (!formData.vehicleCategory) return toast.warning("Vehicle Category is required"), false;
        if (!formData.vehicleNo) return toast.warning("Vehicle Number is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const currentLogin = localStorage.getItem("employeeName") || "Admin";
        const loggedInUserId = localStorage.getItem("employeeCode") || 0;

        const payload = {
            unitId: parseInt(formData.unitId),
            userId: parseInt(loggedInUserId),
            slotId: parseInt(formData.slotId),
            mobileNumber: formData.mobileNo,
            ownerName: formData.employeeName,
            vehicleNumber: formData.vehicleNo,
            vehicleTypeId: parseInt(formData.vehicleCategory),
            createdBy: currentLogin
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: `/ParkingSlotMaster/AssignParmanentSlotToUser`,
                data: payload
            });

            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success(`Parking assigned to user successfully`);
                navigate('/zentcomm/parking-management/parking-Booking');
            } else {
                toast.error(res.message || `Failed to assign parking to user`);
            }
        } catch (error) {
            toast.error("An error occurred while booking parking slot");
        } finally {
            setIsLoading(false);
        }
    };

    const empOptions = employeeList.map(emp => ({label: `${emp.FirstName} ${emp.LastName}`, value: emp.FirstName}));

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection 
                Navbar={true}
                mainHeading="Book Parking Slot"
                subHeading="Reserve a parking slot for a user"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect 
                    label={<>Unit <span className="text-red-500">*</span></>}
                    options={unitList}
                    value={unitList.find(u => u.value === formData.unitId) || null}
                    onChange={handleUnitChange}
                />
                
                <CustomSelect 
                    label={<>Parking Slot <span className="text-red-500">*</span></>}
                    options={parkingSlotList}
                    value={parkingSlotList.find(s => s.value === formData.slotId) || null}
                    onChange={(opt) => setFormData({ ...formData, slotId: opt ? opt.value : "" })}
                    isDisabled={!formData.unitId}
                />
                
                <CustomSelect 
                    label={<>Owner Name <span className="text-red-500">*</span></>}
                    options={empOptions}
                    value={empOptions.find(e => e.value === formData.employeeName) || null}
                    onChange={handleEmployeeChange}
                    isDisabled={!formData.unitId}
                />
                
                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="mobileNo">
                        Mobile No <span className="text-red-500">*</span>
                    </label>
                    <input 
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none bg-gray-50 focus:border-indigo-500 transition-colors" 
                        type="text" 
                        id="mobileNo" 
                        value={formData.mobileNo} 
                        disabled 
                        placeholder="Mobile Number"
                    />
                </div>
                
                <CustomSelect 
                    label={<>Vehicle Category <span className="text-red-500">*</span></>}
                    options={vehicleTypeList}
                    value={vehicleTypeList.find(v => v.value === formData.vehicleCategory) || null}
                    onChange={(opt) => setFormData({ ...formData, vehicleCategory: opt ? opt.value : "" })}
                />
                
                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="vehicleNo">
                        Vehicle Number <span className="text-red-500">*</span>
                    </label>
                    <input 
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors" 
                        type="text" 
                        id="vehicleNo" 
                        value={formData.vehicleNo} 
                        onChange={(e) => setFormData({ ...formData, vehicleNo: e.target.value })}
                        placeholder="e.g. MH12 AB1234"
                    />
                </div>
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentcomm/parking-management/parking-Booking")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Book Slot'}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default ParkingBookingForm;
